// Raw (snake_case, from the backend's row_to_json) → camelCase `LeadDetail`.
// Ported from the portal's `api/leadDetail.ts` WITHOUT the HTTP call so both
// shells can reuse the exact same mapping.
import type { LeadCompleteness } from '@viq-admin/types';
import type { LeadDetail, PersonEditInput, RawnoteEdited } from './types';

// Shape returned by the backend's row_to_json(p.*) — snake_case.
interface PersonRow {
  id?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  company_id?: string;
  conference_id?: string | null;
  card_qr?: string | null;
  status?: string;
  temperature?: string;
  capture_method?: string;
  created_at?: string;
  captured_tz_offset?: string;
  deleted_at?: string | null;
  server_version?: number;
  [key: string]: unknown;
}

interface RawLeadDetail {
  person: PersonRow;
  captured_by_name?: string;
  capturedByName?: string;
  captured_by_oid?: string;
  capturedByOid?: string;
  conference_name?: string;
  conferenceName?: string;
  company_name?: string;
  companyName?: string;
  rawnotes?: Array<{
    id: string;
    text: string;
    summary: string | null;
    original_text?: string | null;
    originalText?: string | null;
    server_version?: number;
    serverVersion?: number;
    audio_duration_ms?: number | null;
    audioDurationMs?: number | null;
    capture_method?: string;
    captureMethod?: string;
    created_at?: string;
    createdAt?: string;
  }>;
  rawphotos?: Array<{
    id: string;
    raw_ocr_text?: string | null;
    rawOcrText?: string | null;
    created_at?: string;
    createdAt?: string;
    has_image?: boolean;
    hasImage?: boolean;
    ai_scanned?: boolean | null;
    aiScanned?: boolean | null;
    toolkitType?: string | null;
    toolkit_type?: string | null;
    deleted_at?: string | null;
    deletedAt?: string | null;
  }>;
  opportunities?: Array<{
    id: string;
    amount_usd?: number | null;
    amountUsd?: number | null;
    stage: string | null;
    source: string;
    created_at?: string;
    createdAt?: string;
  }>;
  tasks?: Array<{
    id: string;
    description: string;
    is_complete?: boolean;
    isComplete?: boolean;
    due_date?: string | null;
    dueDate?: string | null;
    created_at?: string;
    createdAt?: string;
  }>;
  // Company lead rules — the backend sends these already camel-cased.
  completeness?: LeadCompleteness | null;
  crmSentAt?: string | null;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function mapPerson(p: PersonRow): LeadDetail['person'] {
  return {
    id: str(p.id),
    companyId: str(p.company_id),
    conferenceId: str(p.conference_id),
    firstName: str(p.first_name),
    lastName: str(p.last_name),
    displayName: str(p.display_name),
    title: str(p.title),
    company: str(p.company),
    email: str(p.email),
    phone: str(p.phone),
    linkedinUrl: str(p.linkedin_url),
    status: str(p.status),
    temperature: str(p.temperature),
    captureMethod: str(p.capture_method),
    createdAt: str(p.created_at),
    capturedTzOffset: str(p.captured_tz_offset),
    // NOT str() — that collapses null to '', and the panel needs to tell
    // "no code scanned" apart from a real payload.
    cardQr:
      typeof p.cardQr === 'string'
        ? p.cardQr
        : typeof p.card_qr === 'string'
          ? p.card_qr
          : null,
    deletedAt: typeof p.deleted_at === 'string' ? p.deleted_at : null,
    serverVersion: typeof p.server_version === 'number' ? p.server_version : 0,
  };
}

/** Map the raw lead-detail payload to the camel-cased `LeadDetail`. */
export function mapLeadDetail(raw: unknown): LeadDetail {
  const r = (raw ?? {}) as RawLeadDetail;

  return {
    person: mapPerson((r.person ?? {}) as PersonRow),
    capturedByName: str(r.capturedByName ?? r.captured_by_name),
    capturedByOid: str(r.capturedByOid ?? r.captured_by_oid),
    conferenceName: str(r.conferenceName ?? r.conference_name),
    companyName: str(r.companyName ?? r.company_name),
    rawnotes: (r.rawnotes ?? []).map((n) => ({
      id: str(n.id),
      text: str(n.text),
      summary: typeof n.summary === 'string' ? n.summary : null,
      // NOT str() — that collapses null to '', which would make the panel
      // badge a never-edited note as "Edited".
      originalText:
        typeof n.originalText === 'string'
          ? n.originalText
          : typeof n.original_text === 'string'
            ? n.original_text
            : null,
      // 0 is the safe default: a legacy row's PATCH will 409 and refetch
      // rather than silently overwriting on a wrong version.
      serverVersion: n.serverVersion ?? n.server_version ?? 0,
      audioDurationMs: n.audioDurationMs ?? n.audio_duration_ms ?? null,
      captureMethod: str(n.captureMethod ?? n.capture_method),
      createdAt: str(n.createdAt ?? n.created_at),
    })),
    rawphotos: (r.rawphotos ?? []).map((p) => ({
      id: str(p.id),
      rawOcrText: p.rawOcrText ?? p.raw_ocr_text ?? null,
      createdAt: str(p.createdAt ?? p.created_at),
      // Defaults keep an older backend (no card-image fields) rendering as
      // today rather than throwing.
      hasImage: p.hasImage ?? p.has_image ?? false,
      aiScanned: p.aiScanned ?? p.ai_scanned ?? null,
      toolkitType: p.toolkitType ?? p.toolkit_type ?? null,
      deletedAt: p.deletedAt ?? p.deleted_at ?? null,
    })),
    opportunities: (r.opportunities ?? []).map((o) => ({
      id: str(o.id),
      amountUsd: o.amountUsd ?? o.amount_usd ?? null,
      stage: o.stage,
      source: str(o.source),
      createdAt: str(o.createdAt ?? o.created_at),
    })),
    tasks: (r.tasks ?? []).map((t) => ({
      id: str(t.id),
      description: str(t.description),
      isComplete: t.isComplete ?? t.is_complete ?? false,
      dueDate: t.dueDate ?? t.due_date ?? null,
      createdAt: str(t.createdAt ?? t.created_at),
    })),
    // Company lead rules (spec 2026-09-14 §2.2). Copied only when present, so an
    // older backend's payload maps to exactly what it did before, and "not
    // projected" (no key) can never read as "rules off" (null).
    ...(r.completeness !== undefined ? { completeness: r.completeness } : {}),
    ...(r.crmSentAt !== undefined ? { crmSentAt: r.crmSentAt } : {}),
  };
}

// Shape returned by a note-PATCH's RETURNING clause — snake_case, and
// UNALIASED (unlike the lead-detail GET's row_to_json, which the backend
// already camel-cases at the edge). Tolerates camelCase too, for a caller
// that already mapped it.
interface RawnoteEditedRow {
  id?: string;
  text?: string;
  summary?: string | null;
  original_text?: string | null;
  originalText?: string | null;
  server_version?: number;
  serverVersion?: number;
  updated_at?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

/**
 * Map a note-PATCH response (`{ note: <row> }`'s `note`) to the camelCase
 * `RawnoteEdited` the panels consume. Both `apps/portal` and `apps/console`
 * share this — the wire shape is the same unaliased RETURNING on both planes.
 */
export function mapRawnoteEdited(raw: unknown): RawnoteEdited {
  const n = (raw ?? {}) as RawnoteEditedRow;
  return {
    id: str(n.id),
    text: str(n.text),
    summary: typeof n.summary === 'string' ? n.summary : null,
    // NOT str() — that collapses null to '', and the panels distinguish
    // "never admin-edited" (null) from an edit.
    originalText:
      typeof n.originalText === 'string'
        ? n.originalText
        : typeof n.original_text === 'string'
          ? n.original_text
          : null,
    // 0 is the safe default: a legacy/malformed row's next PATCH will 409 and
    // refetch rather than silently overwriting on a wrong version.
    serverVersion: n.serverVersion ?? n.server_version ?? 0,
    updatedAt: str(n.updatedAt ?? n.updated_at),
  };
}

/**
 * The ten editable fields' starting values for a person, as LeadEditForm
 * binds them. Lives here (not in LeadEditForm.tsx) so a caller that owns an
 * external form instance — LeadDetailBody, re-seeding before it opens a
 * FRESH edit session — can import a plain function without pulling in a
 * component (keeps LeadEditForm.tsx component-only for Fast Refresh).
 */
export function personToEditInput(person: LeadDetail['person']): PersonEditInput {
  return {
    firstName: person.firstName ?? '',
    lastName: person.lastName ?? '',
    displayName: person.displayName,
    title: person.title,
    company: person.company,
    email: person.email,
    phone: person.phone,
    linkedinUrl: person.linkedinUrl ?? '',
    status: person.status,
    temperature: person.temperature,
  };
}
